<template>
  <div v-if="pageNaviList" class="p-page-navi">
    <div class="p-page-navi__arrow">
      <component
        :is="prevButtonTag"
        v-bind="prevButtonTag === 'a' ? { href: prevPageUrl } : {}"
        class="p-page-navi__arrow__link"
      >
        <IconBase
          class="p-page-navi__arrow__arrow"
          view-box="0 0 24 24"
          width="24"
          height="24"
          :icon-color="currentPrevLinkColor"
        >
          <IconArrowLeft />
        </IconBase>
      </component>
    </div>
    <ol class="p-page-navi__ol">
      <li
        v-for="(val, key) in pageNaviList"
        :key="key"
        class="p-page-navi__ol__item"
      >
        <ButtonMain
          :tag="currentPage === val ? 'div' : 'a'"
          :href="
            currentPage === val
              ? ''
              : baseUrl +
                '&' +
                pageParam +
                '=' +
                val +
                searchParams +
                orderingParam
          "
          kind="square"
          :gray-out="currentPage !== val"
          :style="
            currentPage === val
              ? {
                  cursor: 'text',
                  color: '#ffffff',
                  backgroundColor: colorCode,
                  border: 'none'
                }
              : ''
          "
          >{{ val }}</ButtonMain
        >

        <div
          v-if="key === 1 && val <= arrayPosition.maxPage - 2"
          class="p-page-navi__ol__col p-page-navi__ol__col--after"
        >
          <span class="p-page-navi__ol__col__dot">...</span>
          <ButtonMain
            tag="a"
            :href="
              baseUrl +
                '&' +
                pageParam +
                '=' +
                maxPage +
                searchParams +
                orderingParam
            "
            kind="square"
            :gray-out="maxPage !== val"
            >{{ maxPage }}</ButtonMain
          >
        </div>
      </li>
    </ol>
    <div class="p-page-navi__arrow">
      <component
        :is="nextButtonTag"
        v-bind="nextButtonTag === 'a' ? { href: nextPageUrl } : {}"
        class="p-page-navi__arrow__link"
      >
        <IconBase
          class="p-page-navi__arrow__arrow"
          view-box="0 0 24 24"
          width="24"
          height="24"
          :icon-color="currentNextLinkColor"
        >
          <IconArrowRight />
        </IconBase>
      </component>
    </div>
  </div>
</template>

<script lang="ts">
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowLeft from "@/common/components/icons/IconArrowLeft.vue";
import IconArrowRight from "@/common/components/icons/IconArrowRight.vue";
import commons from "@/common/store/modules/commons";

import { Options, Vue } from "vue-class-component";
import { mapState } from "vuex";

@Options({
  props: {
    currentPage: { type: Number, default: 1 },
    maxPage: { type: Number, default: 1 },
    nextPageUrl: { type: [String, Boolean], required: true },
    prevPageUrl: { type: [String, Boolean], required: true },
    baseUrl: { type: String, required: true },
    pageParam: { type: String, default: "page" },
    search: { type: String },
    ordering: { type: String }
  },
  components: {
    ButtonMain,
    IconBase,
    IconArrowLeft,
    IconArrowRight
  },
  computed: {
    ...mapState({
      storeColorCode: () => commons.colorCode
    })
  }
})
export default class PageNavi extends Vue {
  currentPage!: number;
  maxPage!: number;
  nextPageUrl!: string;
  prevPageUrl!: string;
  baseUrl!: string;
  pageParam!: string;
  search!: string;
  ordering!: string;
  storeColorCode: string;

  buttonType = "";

  getCurrentColor(pageType: any) {
    const color = {
      disable: "#E2E2E2",
      active: "#000000"
    };

    return pageType === true ? color.disable : color.active;
  }

  isPrevOrNextPageButton(buttonType = "prev") {
    const maxPage = this.maxPage;
    const currentPage = this.currentPage;

    const is = {
      firstPage: currentPage === 1,
      lastPage: currentPage === maxPage
    };

    const pageType = buttonType === "prev" ? is.firstPage : is.lastPage;
    const isPrevOrNextPageButton = pageType;

    return isPrevOrNextPageButton;
  }

  getPageNaviList() {
    const maxPage = this.maxPage;
    const arrayPosition = this.arrayPosition;

    const pagesArray = [...Array(maxPage).keys()].map((i) => ++i);

    const sliceArray = () => {
      const currentPosition = arrayPosition.currentPage;
      const is = {
        firstPage: currentPosition === 0,
        lastPageGroup: currentPosition >= arrayPosition.maxPage - 3,
        lastPage: currentPosition === arrayPosition.maxPage
      };
      let resultArray = [];

      if (is.firstPage) {
        if (maxPage > 4) {
          resultArray = pagesArray.slice(0, 2);
        } else {
          resultArray = pagesArray.slice(0, 4);
        }
      } else if (is.lastPage) {
        resultArray = pagesArray.slice(-4);
      } else if (is.lastPageGroup) {
        resultArray = pagesArray.slice(-4);
      } else {
        resultArray = pagesArray.slice(
          currentPosition - 1,
          currentPosition + 1
        );
      }

      return resultArray;
    };

    return sliceArray();
  }

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return this.storeColorCode;
  }

  get currentPrevLinkColor() {
    return this.resultCurrentLinkColor("prev");
  }

  get currentNextLinkColor() {
    return this.resultCurrentLinkColor("next");
  }

  resultCurrentLinkColor(buttonType = "prev") {
    const isPrevOrNextPageButton = this.isPrevOrNextPageButton(buttonType);
    const currentLinkColor = this.getCurrentColor(isPrevOrNextPageButton);
    return currentLinkColor;
  }

  get prevButtonTag() {
    const isPrevOrNextPageButton = this.isPrevOrNextPageButton("prev");
    const tag = isPrevOrNextPageButton === true ? "span" : "a";
    return tag;
  }

  get nextButtonTag() {
    const isPrevOrNextPageButton = this.isPrevOrNextPageButton("next");
    const tag = isPrevOrNextPageButton === true ? "span" : "a";
    return tag;
  }

  get prevAndNextButtonTag() {
    const isPrevOrNextPageButton = this.isPrevOrNextPageButton();
    const tag = isPrevOrNextPageButton === true ? "span" : "a";
    return tag;
  }

  get pageNaviList() {
    return this.getPageNaviList();
  }

  get arrayPosition() {
    const maxPage = this.maxPage;
    const currentPage = this.currentPage;
    const arrayPosition = {
      currentPage: currentPage - 1,
      maxPage: maxPage - 1
    };

    return arrayPosition;
  }
  get searchParams() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const searchWordFromUrl = urlParams.get("search")
      ? (urlParams.get("search") as string)
      : "";
    return this.search
      ? `&search=${this.search}`
      : `&search=${searchWordFromUrl}`;
  }

  get orderingParam() {
    return this.ordering ? `&ordering=${this.ordering}` : ``;
  }
}
</script>

<style lang="scss" scoped>
.p-page-navi {
  width: rem(280);
  margin: 0 auto;
  display: flex;

  &__arrow {
    &__link {
      display: flex;
      align-items: center;
      justify-content: center;
      width: rem(40);
      height: rem(40);
    }
  }

  &__ol {
    width: rem(200);
    display: flex;
    justify-content: center;
    margin: rem(-8) rem(8) 0 0;
    flex-wrap: wrap;

    &__item {
      margin: rem(8) 0 0 rem(8);
      display: flex;
    }

    &__col {
      display: flex;

      &--before {
      }

      &--after {
      }

      &__dot {
        display: flex;
        width: rem(40);
        height: rem(40);
        align-items: flex-end;
        justify-content: center;
        margin: 0 rem(8);
        padding: rem(9) 0 rem(10);
        box-sizing: border-box;
        line-height: (21 / 14);
      }
    }
  }
}
</style>
